// src/features/health/types/medicationTypes.ts
// Medication-related TypeScript types and interfaces

import { z } from 'zod';

// ============================================================================
// Enums
// ============================================================================

export enum MedicationRoute {
  ORAL = 'oral',
  TOPICAL = 'topical',
  INJECTION = 'injection',
  INHALATION = 'inhalation',
  RECTAL = 'rectal',
  SUBLINGUAL = 'sublingual',
  TRANSDERMAL = 'transdermal',
  OPHTHALMIC = 'ophthalmic',
  OTIC = 'otic',
  NASAL = 'nasal',
  OTHER = 'other',
}

export enum MedicationFrequency {
  ONCE_DAILY = 'once_daily',
  TWICE_DAILY = 'twice_daily',
  THREE_TIMES_DAILY = 'three_times_daily',
  FOUR_TIMES_DAILY = 'four_times_daily',
  EVERY_OTHER_DAY = 'every_other_day',
  WEEKLY = 'weekly',
  BIWEEKLY = 'biweekly',
  MONTHLY = 'monthly',
  AS_NEEDED = 'as_needed',
  CUSTOM = 'custom',
}

export enum AdherenceStatus {
  TAKEN = 'taken',
  MISSED = 'missed',
  SKIPPED = 'skipped',
  LATE = 'late',
  PENDING = 'pending',
}

// ============================================================================
// Core Interfaces
// ============================================================================

export interface Medication {
  id: string;
  childId: string;
  name: string;
  genericName?: string;
  dosage: string;
  frequency: MedicationFrequency;
  route: MedicationRoute;
  prescribingDoctor?: string;
  pharmacy?: string;
  rxNumber?: string;
  startDate?: string;
  endDate?: string;
  instructions?: string;
  sideEffects?: string;
  isActive: boolean;
  reminderEnabled: boolean;
  reminderTimes: string[]; // Array of HH:mm times
  refillDate?: string;
  refillQuantity?: number;
  currentQuantity?: number;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface MedicationDose {
  id: string;
  medicationId: string;
  scheduledTime: string;
  actualTime?: string;
  status: AdherenceStatus;
  notes?: string;
  loggedById: string;
  loggedByName: string;
  createdAt: string;
}

export interface MedicationScheduleItem {
  id: string;
  medication: Medication;
  scheduledTime: string;
  status: AdherenceStatus;
  doseId?: string;
}

export interface MedicationAdherenceStats {
  medicationId: string;
  medicationName: string;
  totalDoses: number;
  takenDoses: number;
  missedDoses: number;
  skippedDoses: number;
  lateDoses: number;
  adherenceRate: number;
  averageDelayMinutes?: number;
}

export interface DailyMedicationSchedule {
  date: string;
  items: MedicationScheduleItem[];
  summary: {
    total: number;
    completed: number;
    pending: number;
    missed: number;
  };
}

export interface MedicationReminder {
  id: string;
  medicationId: string;
  time: string;
  enabled: boolean;
  notificationMethods: ('push' | 'email' | 'sms')[];
  advanceMinutes: number;
}

// ============================================================================
// Form Schemas
// ============================================================================

export const medicationFormSchema = z.object({
  name: z.string().min(1, 'Medication name is required').max(200),
  genericName: z.string().max(200).optional(),
  dosage: z.string().min(1, 'Dosage is required').max(100),
  frequency: z.nativeEnum(MedicationFrequency),
  route: z.nativeEnum(MedicationRoute),
  prescribingDoctor: z.string().max(200).optional(),
  pharmacy: z.string().max(200).optional(),
  rxNumber: z.string().max(100).optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  instructions: z.string().max(1000).optional(),
  sideEffects: z.string().max(1000).optional(),
  isActive: z.boolean().default(true),
  reminderEnabled: z.boolean().default(false),
  reminderTimes: z.array(z.string()).default([]),
  refillDate: z.string().optional(),
  refillQuantity: z.number().int().positive().optional(),
  currentQuantity: z.number().int().min(0).optional(),
  notes: z.string().max(2000).optional(),
});

export type MedicationFormData = z.infer<typeof medicationFormSchema>;

export const medicationDoseFormSchema = z.object({
  status: z.nativeEnum(AdherenceStatus),
  actualTime: z.string().optional(),
  notes: z.string().max(500).optional(),
});

export type MedicationDoseFormData = z.infer<typeof medicationDoseFormSchema>;

// ============================================================================
// API Request/Response Types
// ============================================================================

export interface MedicationListParams {
  childId: string;
  isActive?: boolean;
  search?: string;
  page?: number;
  limit?: number;
}

export interface MedicationListResponse {
  medications: Medication[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface MedicationScheduleParams {
  childId: string;
  date: string;
}

export interface MedicationAdherenceParams {
  childId: string;
  medicationId?: string;
  startDate: string;
  endDate: string;
}

export interface LogMedicationDoseRequest {
  medicationId: string;
  scheduledTime: string;
  status: AdherenceStatus;
  actualTime?: string;
  notes?: string;
}

// ============================================================================
// Display Helpers
// ============================================================================

export const frequencyLabels: Record<MedicationFrequency, string> = {
  [MedicationFrequency.ONCE_DAILY]: 'Once daily',
  [MedicationFrequency.TWICE_DAILY]: 'Twice daily',
  [MedicationFrequency.THREE_TIMES_DAILY]: 'Three times daily',
  [MedicationFrequency.FOUR_TIMES_DAILY]: 'Four times daily',
  [MedicationFrequency.EVERY_OTHER_DAY]: 'Every other day',
  [MedicationFrequency.WEEKLY]: 'Weekly',
  [MedicationFrequency.BIWEEKLY]: 'Biweekly',
  [MedicationFrequency.MONTHLY]: 'Monthly',
  [MedicationFrequency.AS_NEEDED]: 'As needed',
  [MedicationFrequency.CUSTOM]: 'Custom schedule',
};

export const routeLabels: Record<MedicationRoute, string> = {
  [MedicationRoute.ORAL]: 'Oral',
  [MedicationRoute.TOPICAL]: 'Topical',
  [MedicationRoute.INJECTION]: 'Injection',
  [MedicationRoute.INHALATION]: 'Inhalation',
  [MedicationRoute.RECTAL]: 'Rectal',
  [MedicationRoute.SUBLINGUAL]: 'Sublingual',
  [MedicationRoute.TRANSDERMAL]: 'Transdermal',
  [MedicationRoute.OPHTHALMIC]: 'Eye drops',
  [MedicationRoute.OTIC]: 'Ear drops',
  [MedicationRoute.NASAL]: 'Nasal',
  [MedicationRoute.OTHER]: 'Other',
};

export const adherenceStatusLabels: Record<AdherenceStatus, string> = {
  [AdherenceStatus.TAKEN]: 'Taken',
  [AdherenceStatus.MISSED]: 'Missed',
  [AdherenceStatus.SKIPPED]: 'Skipped',
  [AdherenceStatus.LATE]: 'Taken late',
  [AdherenceStatus.PENDING]: 'Pending',
};

export const adherenceStatusColors: Record<AdherenceStatus, string> = {
  [AdherenceStatus.TAKEN]: 'text-green-600 bg-green-50',
  [AdherenceStatus.MISSED]: 'text-red-600 bg-red-50',
  [AdherenceStatus.SKIPPED]: 'text-amber-600 bg-amber-50',
  [AdherenceStatus.LATE]: 'text-orange-600 bg-orange-50',
  [AdherenceStatus.PENDING]: 'text-gray-600 bg-gray-50',
};

